/* import __COLOCATED_TEMPLATE__ from './address-form.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import States from 'embercom/lib/states';
import Countries from 'embercom/lib/countries';
import { countrySelect } from 'embercom/helpers/country-select-helper';
import AddressSettings from 'embercom/lib/address-settings';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import nonEuTaxEligibleCountries from 'embercom/lib/non-eu-tax-eligible-countries';
import { validateVatNumber, getVatValidationErrorKey } from 'embercom/utils/vat-validation';

export default class AddressForm extends Component {
  @tracked states;
  @tracked addressConfiguration;
  @tracked showVatNumber;
  @tracked vatValidationErrorKey = null;
  @service intl;
  @service appService;

  constructor() {
    super(...arguments);

    this.addressConfiguration =
      AddressSettings[this.args.address.countryCode] || AddressSettings.default;

    if (this.addressConfiguration.showState) {
      this.states = States[this.args.address.countryCode];
    }

    this.showVatNumber = this.shouldCollectVatNumber(this.args.address.countryCode);
  }

  get app() {
    return this.appService.app;
  }

  get countries() {
    return countrySelect(Countries.allCountries);
  }

  get isCountryCodeInvalid() {
    return isEmpty(this.args.address.countryCode);
  }

  get isStreetAddressInvalid() {
    return isEmpty(this.args.address.streetAddress);
  }

  get isCityInvalid() {
    return isEmpty(this.args.address.city);
  }

  get isStateCodeInvalid() {
    return isEmpty(this.args.address.stateCode);
  }

  get isZipcodeInvalid() {
    let postcode = this.args.address.postcode;
    return isEmpty(postcode) || !this.validPostcodeFormat();
  }

  get zipcodeErrorMessage() {
    let postcode = this.args.address.postcode;
    let localizedPostCodeLabel = this.intl.t(this.addressConfiguration.postCodeLabelTranslationKey);
    if (isEmpty(postcode)) {
      return this.intl.t('billing.address-form.post-code-blank', {
        postCodeLabel: localizedPostCodeLabel,
      });
    } else if (!this.validPostcodeFormat()) {
      return this.intl.t('billing.address-form.post-code-invalid', {
        postCodeLabel: localizedPostCodeLabel,
      });
    }
    return '';
  }

  validPostcodeFormat() {
    if (!this.addressConfiguration.postCodeRegex) {
      return true;
    }

    return this.addressConfiguration.postCodeRegex.exec(this.args.address.postcode);
  }

  placeholderKey() {
    let countryCode = this.args.address.countryCode;
    let baseKey = 'signup.teams.pricing5.annual-plans.credit-card-details';

    let placeholderCountry = 'default';
    if (countryCode && nonEuTaxEligibleCountries.includes(countryCode)) {
      placeholderCountry = countryCode.toLowerCase();
    }

    return `${baseKey}.${placeholderCountry}-tax-number-placeholder`;
  }

  /**
   * Returns the translated placeholder text
   */
  get placeholder() {
    return this.intl.t(this.placeholderKey(), { countryCode: this.args.address.countryCode });
  }

  get isVatInvalid() {
    // Combine frontend validation errors with backend validation errors
    return !!this.vatValidationErrorKey || !!this.args.isVatInvalid;
  }

  get vatErrorMessage() {
    // Priority: frontend validation error first, then backend error
    if (this.vatValidationErrorKey) {
      return this.intl.t(this.vatValidationErrorKey);
    }
    if (this.args.isVatInvalid) {
      // Backend validation error - use a different translation key
      return this.intl.t('billing.address-form.vat-backend-error');
    }
    return null;
  }

  @action
  onCountrySelected(code) {
    this.addressConfiguration = AddressSettings[code] || AddressSettings.default;

    if (this.addressConfiguration.showState) {
      this.states = States[code];
    }
    this.args.address.stateCode = undefined;

    this.showVatNumber = this.shouldCollectVatNumber(code);
    if (!this.showVatNumber) {
      this.args.address.vatNumber = undefined;
      this.vatValidationErrorKey = null; // Clear validation error when VAT field is hidden
    } else {
      // Re-validate existing VAT number if country changes
      let vatNumber = this.args.address.vatNumber;
      if (vatNumber && !validateVatNumber(vatNumber, code)) {
        this.vatValidationErrorKey = getVatValidationErrorKey(code);
      } else {
        this.vatValidationErrorKey = null;
      }
    }

    // Notify parent about validation state change
    if (this.args.onVatValidationChange) {
      this.args.onVatValidationChange(!!this.vatValidationErrorKey);
    }

    this.args.address.countryCode = code;
  }

  @action
  onStateCodeSelected(code) {
    this.args.address.stateCode = code;
    // re-calculate if we should show the VAT number field & re-render the component
    this.showVatNumber = this.shouldCollectVatNumber(this.args.address.countryCode);
  }

  @action
  onVatNumberInput(event) {
    let vatNumber = event.target.value;
    let countryCode = this.args.address.countryCode;

    // Update the VAT number value
    this.args.address.vatNumber = vatNumber;

    // Only validate frontend if there's no backend error
    // Backend errors take precedence and should be cleared by backend response
    if (!this.args.isVatInvalid) {
      // Validate the VAT number
      if (vatNumber && !validateVatNumber(vatNumber, countryCode)) {
        this.vatValidationErrorKey = getVatValidationErrorKey(countryCode);
      } else {
        this.vatValidationErrorKey = null;
      }
    } else {
      // Clear frontend validation error when backend error exists
      this.vatValidationErrorKey = null;
    }

    // Notify parent about validation state change
    if (this.args.onVatValidationChange) {
      this.args.onVatValidationChange(!!this.vatValidationErrorKey);
    }

    // Emit the change if there's a callback
    if (this.args.onVatChange) {
      this.args.onVatChange();
    }
  }

  @action
  emitVatChange() {
    if (this.args.onVatChange) {
      this.args.onVatChange();
    }
  }

  shouldCollectVatNumber(code) {
    if (!this.args.collectVatNumber || !code) {
      return false;
    }

    let country = Countries.allCountries.find((country) => country.code === code);

    let secondWaveCountries = this.appService.app.canUseFeature('tax-collection-second-wave')
      ? ['NG']
      : [];
    return (
      country.inEu ||
      nonEuTaxEligibleCountries.includes(country.code) ||
      secondWaveCountries.includes(country.code)
    );
  }

  get styles() {
    if (
      !this.shouldCollectVatNumber(this.args.address.countryCode) &&
      !this.addressConfiguration.showState
    ) {
      return 'gap-x-6 gap-y-4 grid grid-cols-1';
    }
    return 'gap-x-6 gap-y-4 grid grid-cols-2';
  }
}
